/**
 * InputText Component - Controller
 * ================================
 */

import type { IScope, ISCEService, INgModelController, ITranscludeFunction } from 'angular';
import {
  SIZES,
  BASE_CLASSES,
  ICONS,
  DEFAULTS,
  VALID_TYPES,
  InputTextLabelPosition,
  InputTextSize,
  InputTextIconType,
  InputTextType
} from './constants';

export interface InputTextBindings {
  label?: string;
  labelPosition?: InputTextLabelPosition;
  placeholder?: string;
  icon?: InputTextIconType;
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: InputTextSize;
  inputId?: string;
  // Atributos nativos del <input> (autofill / gestores de contraseña / teclado móvil).
  type?: InputTextType;
  name?: string;
  autocomplete?: string;
  onChange?: (args: { value: string }) => void;
}

export class InputTextController implements InputTextBindings {
  static $inject = ['$scope', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  // required controller (?ngModel)
  ngModelCtrl?: INgModelController | null;

  // bindings
  label?: string;
  labelPosition?: InputTextLabelPosition;
  placeholder?: string;
  icon?: InputTextIconType;
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: InputTextSize;
  inputId?: string;
  type?: InputTextType;
  name?: string;
  autocomplete?: string;
  onChange?: (args: { value: string }) => void;

  // internal
  inputValue = '';
  trustedIcon: unknown = null;
  resolvedId = '';
  helpId = '';

  // Slots nombrados (label/help): detectados con isSlotFilled, que SÍ funciona
  // para multi-slot transclusion (a diferencia del slot default booleano).
  hasLabelSlot = false;
  hasHelpSlot = false;

  constructor($scope: IScope, $sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$scope = $scope;
    this.$sce = $sce;
    this.$transclude = $transclude;
  }

  $onInit(): void {
    this.labelPosition = this.labelPosition || DEFAULTS.labelPosition;
    this.size = this.size || DEFAULTS.size;
    this.icon = this.icon || DEFAULTS.icon;
    // type inválido (p. ej. 'password' → usar ec-input-password) degrada a 'text'.
    if (this.type && VALID_TYPES.indexOf(this.type) === -1) {
      console.warn(
        `[ec-input-text] type "${this.type}" no soportado; usando "text". ` +
          'Para contraseñas usá ec-input-password.'
      );
    }
    this.type = this.type && VALID_TYPES.indexOf(this.type) !== -1 ? this.type : DEFAULTS.type;

    const uid = this.$scope ? String(this.$scope.$id) : Math.random().toString(36).slice(2);
    this.resolvedId = this.inputId || ('ec-input-' + uid);
    this.helpId = 'ec-input-help-' + uid;

    if (this.$transclude) {
      this.hasLabelSlot = this.$transclude.isSlotFilled('labelSlot');
      this.hasHelpSlot = this.$transclude.isSlotFilled('helpSlot');
    }

    this._updateTrusted();

    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => {
        const v = this.ngModelCtrl ? this.ngModelCtrl.$viewValue : null;
        this.inputValue = v != null ? String(v) : '';
      };
    }
  }

  $onChanges(changes: Record<string, { currentValue: unknown }>): void {
    if (changes.icon) {
      this._updateTrusted();
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$sce = null;
    this.$transclude = null;
    this.ngModelCtrl = null;
  }

  private _updateTrusted(): void {
    if (this.$sce) {
      this.trustedIcon = this.$sce.trustAsHtml(ICONS[this.icon as InputTextIconType] || '');
    }
  }

  handleInput(): void {
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(this.inputValue);
    }
    if (typeof this.onChange === 'function') {
      this.onChange({ value: this.inputValue });
    }
  }

  getWrapperClasses(): string[] {
    return this.labelPosition === 'inline'
      ? ['ec-flex', 'ec-flex-row', 'ec-items-center', 'ec-gap-3']
      : ['ec-flex', 'ec-flex-col', 'ec-gap-1'];
  }

  getControlClasses(): string[] {
    const size = SIZES[this.size || DEFAULTS.size];
    const classes = [BASE_CLASSES.control, size.control, size.text];
    if (this.disabled) classes.push(BASE_CLASSES.controlDisabled);
    return classes;
  }

  // Figma no pinta borde en error: el color va en el texto del valor y del help.
  getInputClasses(): string[] {
    const classes = [BASE_CLASSES.input];
    if (this.disabled) {
      classes.push(BASE_CLASSES.inputDisabled);
    } else if (this.error) {
      classes.push(BASE_CLASSES.inputError);
    } else {
      classes.push(BASE_CLASSES.inputColor);
    }
    return classes;
  }

  getLabelClasses(): string[] {
    return this.labelPosition === 'inline'
      ? [BASE_CLASSES.label, BASE_CLASSES.labelInline]
      : [BASE_CLASSES.label];
  }

  getHelpClasses(): string[] {
    return this.error
      ? [BASE_CLASSES.help, BASE_CLASSES.helpError]
      : [BASE_CLASSES.help, BASE_CLASSES.helpColor];
  }

  getIconClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].icon, BASE_CLASSES.icon];
  }

  get hasIcon(): boolean {
    return !!this.icon && this.icon !== 'none';
  }
}

export default InputTextController;
